import React, { useState, useEffect, useCallback } from "react";
import { Lock, Unlock, RefreshCw, Search, Users } from "lucide-react";
import { userManagementApi } from "../../services/userManagementApi";
import { UserAccountItem } from "./types";

export const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserAccountItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchUsers = useCallback(async (searchQuery?: string) => {
    setLoading(true);
    try {
      const data = await userManagementApi.getDirectory(undefined, searchQuery);
      if (Array.isArray(data)) {
        const mapped: UserAccountItem[] = data.map((u: {
          id: string;
          username?: string;
          first_name?: string;
          last_name?: string;
          email: string;
          role?: string;
          is_active?: boolean;
          date_joined?: string;
          extended_profile?: { department?: string };
        }) => {
          const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || u.email;
          return {
            id: u.id,
            name: fullName,
            email: u.email,
            department: u.extended_profile?.department || "General Academic",
            role: (u.role || "User").toUpperCase(),
            status: u.is_active !== false ? "Active" : "Deactivated",
            lastLogin: u.date_joined ? new Date(u.date_joined).toLocaleDateString() : "Recent",
          };
        });
        setUsers(mapped);
      }
    } catch (err) {
      console.warn("[UserManagement] Directory fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchUsers]);

  const handleToggleStatus = async (user: UserAccountItem) => {
    setActionLoading(user.id);
    try {
      const eventType = user.status === "Active" ? "deactivation" : "activation";
      await userManagementApi.transitionLifecycle(user.id as any, eventType, "Status toggled via User Management console");
      fetchUsers(search);
    } catch (err) {
      console.error("Failed to toggle user status:", err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">User Account Provisioning & Identity Directory</h2>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 font-mono">
            Total Accounts: <span className="text-indigo-400 font-bold">{users.length}</span>
          </span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter users by name, email, username, or assigned role..."
          className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-200 text-xs font-medium focus:outline-none focus:border-indigo-500"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-950">
        <table className="w-full text-left text-xs text-slate-300">
          <thead className="bg-slate-900 text-[10px] font-mono uppercase text-slate-400 border-b border-slate-800">
            <tr>
              <th className="p-3">User Name & Email</th>
              <th className="p-3">Department</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">Registration Date</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 font-mono">
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">Loading identity directory...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-500">No user accounts found matching your query.</td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-850">
                  <td className="p-3">
                    <div className="font-bold text-slate-100 font-sans">{u.name}</div>
                    <div className="text-[10px] text-slate-400">{u.email}</div>
                  </td>
                  <td className="p-3 font-sans text-slate-300">{u.department}</td>
                  <td className="p-3 font-bold text-indigo-400 font-sans">{u.role}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        u.status === "Active"
                          ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
                          : "bg-rose-950 text-rose-300 border border-rose-800"
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="p-3 text-slate-400">{u.lastLogin}</td>
                  <td className="p-3 text-right font-sans">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => handleToggleStatus(u)}
                        disabled={actionLoading === u.id}
                        className={`p-1.5 rounded transition-colors ${
                          u.status === "Active"
                            ? "text-slate-400 hover:text-rose-400 hover:bg-rose-950/30"
                            : "text-slate-400 hover:text-emerald-400 hover:bg-emerald-950/30"
                        }`}
                        title={u.status === "Active" ? "Deactivate Account" : "Activate Account"}
                      >
                        {actionLoading === u.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : u.status === "Active" ? (
                          <Lock className="w-3.5 h-3.5" />
                        ) : (
                          <Unlock className="w-3.5 h-3.5 text-emerald-400" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
